import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getQuestionnaire } from '../../lib/firebase';
import { useAuth } from '../../lib/AuthContext';
import { useQuestionnaire } from '../../lib/QuestionnaireContext';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../ui/Card';
import { Button } from '../ui/Button';
import { Skeleton } from '../ui/Skeleton';
import { Page, PageSection } from './PageStructureBuilder';
import { WebsiteReference } from './WebsiteReferenceBuilder';
import { convertTemplateToSummary, FieldDefinition } from '../../lib/summaryUtils';



// Define a mapping of field values to their corresponding labels
const fieldValueMappings: Record<string, Record<string, string>> = {
  // Project Categories
  projectCategories: {
    'ecommerce': 'Ecommerce',
    'portfolio': 'Portfolio',
    'restaurant': 'Restaurant',
    'realEstate': 'Real Estate',
    'hospitality': 'Hotels/Hospitality',
    'corporate': 'Corporate/Business/Non-profit',
    'blog': 'Blog',
    'events': 'Events',
    'other': 'Other'
  },
  
  // Timeline
  timeline: {
    'urgent': 'Urgent (< 1 month)',
    'short': 'Short (1-3 months)',
    'medium': 'Medium (3-6 months)',
    'long': 'Long (6+ months)',
    'flexible': 'Flexible'
  },
  
  // Budget
  budget: {
    '10k-20k': '$10,000 - $20,000',
    '20k-35k': '$20,000 - $35,000',
    'over35k': '$35,000+',
    'undetermined': 'Still figuring out / Prefer not to answer'
  },
  
  // Existing Sitemap
  existingSitemap: {
    'yes': 'Yes',
    'no': 'No',
    'inProgress': 'In progress'
  },
  
  // Ecommerce Features
  ecommerceProductCount: {
    'less-than-5': 'Less than 5',
    'less-than-20': 'Less than 20',
    'less-than-50': 'Less than 50',
    'more-than-50': 'More than 50'
  },
  
  ecommerceDiscounts: {
    'subscriptions': 'Subscriptions',
    'bundling': 'Bundling Discounts',
    'both': 'Both',
    'none': 'None'
  },
  
  ecommerceWholesale: {
    'yes': 'Yes',
    'no': 'No'
  },
  
  ecommerceStoreLocator: {
    'yes': 'Yes',
    'no': 'No'
  },
  
  // Restaurant Features
  restaurantReservations: {
    'yes': 'Yes',
    'no': 'No'
  },
  
  restaurantReservationSystem: {
    'resy': 'Resy',
    'opentable': 'Open Table',
    'other': 'Other/TBD'
  },
  
  restaurantMenuPresentation: {
    'pdf': 'Uploadable PDFs',
    'cms': 'Editable in the CMS'
  },
  
  // Real Estate Features
  realEstatePropertyManagement: {
    'yes': 'Yes',
    'no': 'No'
  },
  
  realEstateDisplayUpdates: {
    'yes': 'Yes',
    'no': 'No'
  },
  
  realEstateLeadsToCRM: {
    'yes': 'Yes',
    'no': 'No'
  },
  
  realEstateDisplayMap: {
    'yes': 'Yes',
    'no': 'No'
  },
  
  realEstateMapType: {
    'google': 'Google Map',
    'illustrated': 'Illustrated Map',
    'custom': 'Custom Interactive Map'
  },
  
  // Hotels/Hospitality Features
  hospitalityBookingSystem: {
    'yes': 'Yes',
    'no': 'No',
    'recommend': 'I would like SDCO to recommend one'
  },
  
  hospitalityDisplayMap: {
    'yes': 'Yes',
    'no': 'No'
  },
  
  hospitalityMapType: {
    'google': 'Google Map',
    'illustrated': 'Illustrated Map',
    'custom': 'Custom Interactive Map'
  },
  
  // Events Features
  eventsSellTickets: {
    'yes': 'Yes',
    'no': 'No'
  },
  
  eventsThirdParty: {
    'yes': 'Yes',
    'no': 'No, I want to sell tickets directly to users without a third party'
  },
  
  eventsThirdPartyName: {
    'eventbrite': 'Eventbrite',
    'ticketmaster': 'Ticketmaster',
    'meetup': 'Meetup',
    'other': 'Other'
  },
  
  eventsRequireRSVP: {
    'yes': 'Yes',
    'no': 'No, I just want to display upcoming events with details'
  },
  
  // Content Management
  initialContentUpload: {
    'default': 'Homepage, interior page, and example product or post (e.g: portfolio, article, etc.)',
    'top-level': 'I would like SDCO to upload all top level pages (e.g: Home, About, Services, etc.)',
    'all': 'I would like all content to be uploaded by SDCO'
  },
  
  contentUpdateFrequency: {
    'frequent': 'Frequent (multiple times per week)',
    'sometimes': 'Sometimes (a couple times per month)',
    'rarely': 'Rarely (a couple times per year)'
  },
  
  contentUpdateManagement: {
    'self': 'I am comfortable making these updates',
    'team': 'I have a team to support with these updates',
    'sdco': 'I would like SDCO to make these updates for me'
  },
  
  // Add new mapping for existing content
  existingContent: {
    'reuse': 'Yes, I would like to reuse existing content.',
    'revise': 'Yes, I would like SDCO to revise existing content.',
    'none': 'No, I do not have any existing content.'
  }
};

// Add a helper function to render page structure
const renderPageStructure = (pageStructure: Page[] | undefined) => {
  if (!pageStructure || pageStructure.length === 0) {
    return <p className="text-sm text-gray-500">No page structure defined</p>;
  }

  // Helper function to format description text with line breaks
  const formatDescription = (text: string) => {
    if (!text) return null;
    
    return (
      <div className="whitespace-pre-wrap text-sm text-gray-600">
        {text}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {pageStructure.map((page: Page) => (
        <div key={page.id} className="border rounded-md p-3">
          <h4 className="font-medium">{page.title || 'Untitled Page'}</h4>
          {page.description && formatDescription(page.description)}
          
          {/* Sections */}
          {page.sections.length > 0 && (
            <div className="mt-2">
              <h5 className="text-sm font-medium">Sections:</h5>
              <ul className="list-disc pl-5 mt-1">
                {page.sections.map((section: PageSection) => (
                  <li key={section.id} className="text-sm">
                    <span className="font-medium">{section.title || 'Untitled Section'}</span>
                    {section.description && formatDescription(section.description)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          

        </div>
      ))}
    </div>
  );
};

// Enhanced renderFieldValue function for dynamic fields
const renderFieldValue = (field: FieldDefinition, value: any) => {
  // Handle custom renderers first
  if (field.isCustom && field.renderer) {
    return field.renderer(value);
  }
  
  // Handle file uploads
  if (field.isFile && value) {
    if (typeof value === 'string' && value.startsWith('data:image')) {
      return (
        <div className="mt-2 border rounded p-2 max-w-xs">
          <img 
            src={value} 
            alt="Uploaded file" 
            className="max-w-full h-auto" 
          />
        </div>
      );
    }
    return <p>File uploaded</p>;
  }
  
  // Handle website reference arrays (special case for legacy data)
  if (field.key === 'competitorWebsites' || field.key === 'inspirationalWebsites' || field.key === 'dislikedWebsites') {
    if (!Array.isArray(value) || value.length === 0) {
      return <p>None provided</p>;
    }
    
    return (
      <ul className="list-disc list-inside space-y-2">
        {value.map((website: WebsiteReference, index: number) => (
          <li key={website.id || index} className="ml-2">
            <div>
              <a href={website.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                {website.url}
              </a>
              {website.description && (
                <p className="text-sm text-gray-600 ml-4 mt-1 whitespace-pre-wrap">{website.description}</p>
              )}
            </div>
          </li>
        ))}
      </ul>
    );
  }
  
  // Handle arrays (including checkboxes)
  if (field.isArray && Array.isArray(value)) {
    if (value.length === 0) {
      return <p>None selected</p>;
    }
    
    // Use field options if available
    if (field.options && field.options.length > 0) {
      return (
        <ul className="list-disc list-inside">
          {value.map((item: string, i: number) => {
            const option = field.options!.find(opt => opt.value === item);
            return (
              <li key={i} className="ml-2">{option ? option.label : item}</li>
            );
          })}
        </ul>
      );
    }
    
    // Fallback to legacy mappings
    const mapping = fieldValueMappings[field.key];
    if (mapping) {
      return (
        <ul className="list-disc list-inside">
          {value.map((item: string, i: number) => (
            <li key={i} className="ml-2">{mapping[item] || item}</li>
          ))}
        </ul>
      );
    }
    
    return (
      <ul className="list-disc list-inside">
        {value.map((item: string, i: number) => (
          <li key={i} className="ml-2">{item}</li>
        ))}
      </ul>
    );
  }
  
  // Handle boolean values
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  
  // Handle string values with options mapping
  if (typeof value === 'string' && value) {
    // Use field options if available
    if (field.options && field.options.length > 0) {
      const option = field.options.find(opt => opt.value === value);
      if (option) {
        return <p className="break-words">{option.label}</p>;
      }
    }
    
    // Fallback to legacy mappings
    const mapping = fieldValueMappings[field.key];
    if (mapping && mapping[value]) {
      return <p className="break-words">{mapping[value]}</p>;
    }
    
    return <p className="break-words">{value}</p>;
  }
  
  // Default case
  return <p className="break-words">Not provided</p>;
};

const AdminQuestionnaireView: React.FC = () => {
  const { userId, questionnaireId } = useParams<{ userId: string; questionnaireId: string }>();
  const { currentUser } = useAuth();
  const { isAdmin, activeTemplate } = useQuestionnaire();
  const [questionnaire, setQuestionnaire] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Generate dynamic sections from the active template - must be before any conditional returns
  const sections = useMemo(() => {
    if (!activeTemplate || !questionnaire) {
      return [];
    }

    const dynamicSections = convertTemplateToSummary(activeTemplate, questionnaire.formData);
    
    // Add custom renderers for special field types
    return dynamicSections.map(section => ({
      ...section,
      fields: section.fields.map(field => {
        if (field.key === 'pageStructure') {
          return {
            ...field,
            isCustom: true,
            renderer: renderPageStructure
          };
        }
        return field;
      })
    }));
  }, [activeTemplate, questionnaire]);

  useEffect(() => {
    const fetchQuestionnaire = async () => {
      if (!currentUser || !isAdmin || !userId || !questionnaireId) {
        setError("Unauthorized or missing parameters");
        setLoading(false);
        return;
      }

      try {
        console.log(`Fetching questionnaire for user ${userId} with ID ${questionnaireId}`);
        const data = await getQuestionnaire(userId, questionnaireId);
        console.log('Questionnaire data received:', data);
        
        if (data) {
          setQuestionnaire(data);
        } else {
          console.error(`Questionnaire not found for user ${userId} with ID ${questionnaireId}`);
          setError("Questionnaire not found");
        }
      } catch (err) {
        console.error("Error fetching questionnaire:", err);
        setError(`Failed to load questionnaire: ${err instanceof Error ? err.message : 'Unknown error'}`);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestionnaire();
  }, [currentUser, isAdmin, userId, questionnaireId]);

  const formatDate = (date: Date | null) => {
    if (!date) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const goBack = () => {
    navigate('/questionnaire');
  };

  if (!isAdmin) {
    console.log("Not an admin user, redirecting to home");
    navigate('/questionnaire');
    return null;
  }






  // Render skeleton UI for loading state
  const renderSkeletonView = () => (
    <div className="container max-w-5xl mx-auto p-4">
      <Card className="mb-6">
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <Skeleton className="h-7 w-64 mb-1" />
            </div>
            <Skeleton className="h-9 w-32" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <Skeleton className="h-4 w-20 mb-2" />
              <Skeleton className="h-5 w-32" />
            </div>
            <div>
              <Skeleton className="h-4 w-20 mb-2" />
              <Skeleton className="h-5 w-32" />
            </div>
          </div>

          {sections.map((section, index) => (
            (!section.conditional || (questionnaire && section.conditional(questionnaire.formData))) && (
              <div key={index} className="mb-8">
                <Skeleton className="h-6 w-40 mb-4" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                  {section.fields.map((field, fieldIndex) => (
                    <div key={fieldIndex} className="mb-4">
                      <Skeleton className="h-4 w-24 mb-2" />
                      <Skeleton className="h-5 w-full" />
                      {fieldIndex % 3 === 0 && <Skeleton className="h-5 w-3/4 mt-1" />}
                    </div>
                  ))}
                </div>
              </div>
            )
          ))}

        </CardContent>
      </Card>
    </div>
  );

  if (loading) {
    return renderSkeletonView();
  }

  if (error || !questionnaire) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground">{error || "Failed to load questionnaire"}</p>
          <Button onClick={goBack} className="mt-4">Back to Dashboard</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="container max-w-5xl mx-auto p-4">
      <Card className="mb-6">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>
              Questionnaire Details
              {questionnaire.formData.company && (
                <span className="ml-2 text-muted-foreground">
                  - {questionnaire.formData.company}
                </span>
              )}
            </CardTitle>
            <Button variant="outline" onClick={goBack}>
              Back to Dashboard
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <p className="text-sm text-muted-foreground">Submitted</p>
              <p>{formatDate(questionnaire.submittedAt)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Created</p>
              <p>{formatDate(questionnaire.createdAt)}</p>
            </div>
          </div>

          {sections.map((section, index) => (
            (!section.conditional || section.conditional(questionnaire.formData)) && (
              <div key={index} className="mb-8">
                <h3 className="text-lg font-semibold mb-4 border-b pb-2">{section.title}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                  {section.fields.map((field) => (
                    <div key={field.key} className="mb-4">
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">{field.label}</h4>
                      {renderFieldValue(field, questionnaire.formData[field.key])}
                    </div>
                  ))}
                </div>
              </div>
            )
          ))}

          </CardContent>
      </Card>
    </div>
  );
};

export default AdminQuestionnaireView; 